import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NeighborhoodContext = ({ neighborhood }) => {
  const [selectedRadius, setSelectedRadius] = useState(2);
  const [activeCategory, setActiveCategory] = useState('all');

  const radiusOptions = [1, 2, 3, 6];
  const categories = [
    { id: 'all', label: 'All', icon: 'MapPin' },
    { id: 'transport', label: 'Transport', icon: 'Bus' },
    { id: 'education', label: 'Education', icon: 'GraduationCap' },
    { id: 'healthcare', label: 'Healthcare', icon: 'Heart' },
    { id: 'shopping', label: 'Shopping', icon: 'ShoppingBag' },
    { id: 'dining', label: 'Dining', icon: 'Utensils' },
    { id: 'entertainment', label: 'Entertainment', icon: 'Film' }
  ];

  const getFilteredAmenities = () => {
    if (activeCategory === 'all') {
      return neighborhood.nearbyAmenities;
    }
    return neighborhood.nearbyAmenities.filter(amenity => amenity.category === activeCategory);
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      transport: 'Bus',
      education: 'GraduationCap',
      healthcare: 'Heart',
      shopping: 'ShoppingBag',
      dining: 'Utensils',
      entertainment: 'Film',
      fitness: 'Dumbbell',
      banking: 'CreditCard'
    };
    return iconMap[category] || 'MapPin';
  };

  const getDistanceColor = (distance) => {
    if (distance <= 1) return 'text-success';
    if (distance <= 3) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-surface rounded-lg shadow-property-card">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary mb-4">
          Neighborhood Context
        </h2>
        
        {/* Radius Selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-text-secondary">Show within:</span>
          {radiusOptions.map((radius) => (
            <button
              key={radius}
              onClick={() => setSelectedRadius(radius)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-smooth ${
                selectedRadius === radius
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary hover:text-text-primary'
              }`}
            >
              {radius}km
            </button>
          ))}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                activeCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={category.icon} size={14} />
              <span>{category.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Map */}
        <div className="mb-6">
          <div className="h-64 bg-muted rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Neighborhood Map"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${neighborhood.coordinates.lat},${neighborhood.coordinates.lng}&z=14&output=embed`}
            />
          </div>
        </div>

        {/* Nearby Amenities */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary">
            Nearby Amenities ({getFilteredAmenities().length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {getFilteredAmenities().map((amenity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={getCategoryIcon(amenity.category)} size={16} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">{amenity.name}</div>
                    <div className="text-sm text-text-secondary">{amenity.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-medium ${getDistanceColor(amenity.distance)}`}>
                    {amenity.distance}km
                  </div>
                  <div className="text-xs text-text-secondary">{amenity.walkTime} walk</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commute Calculator */}
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Commute to Business Districts
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {neighborhood.commuteOptions.map((commute, index) => (
              <div key={index} className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-text-primary">{commute.destination}</div>
                  <div className="text-sm text-text-secondary">{commute.distance}km</div>
                </div>
                
                <div className="space-y-2">
                  {commute.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Icon name={option.mode === 'car' ? 'Car' : option.mode === 'metro' ? 'Train' : 'Bus'} size={14} />
                        <span className="capitalize">{option.mode}</span>
                      </div>
                      <div className="text-text-secondary">
                        {option.time} • ₹{option.cost}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lifestyle Insights */}
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Lifestyle Insights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-primary mb-1">
                {neighborhood.walkScore}
              </div>
              <div className="text-sm text-text-secondary">Walk Score</div>
              <div className="text-xs text-text-secondary mt-1">Very Walkable</div>
            </div>
            
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-secondary mb-1">
                {neighborhood.safetyRating}
              </div>
              <div className="text-sm text-text-secondary">Safety Rating</div>
              <div className="text-xs text-text-secondary mt-1">Above Average</div>
            </div>
            
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-accent mb-1">
                {neighborhood.noiseLevel}
              </div>
              <div className="text-sm text-text-secondary">Noise Level</div>
              <div className="text-xs text-text-secondary mt-1">Moderate</div>
            </div>
          </div>
        </div>

        {/* Explore Neighborhood Button */}
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            iconName="ExternalLink"
            iconPosition="right"
          >
            Explore Full Neighborhood Guide
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NeighborhoodContext;